// What the crafting panel shows, worked out from the recipe list the server sends: which tiles there
// are, which group each sits in, what each one shows, and what a recipe on the bench is short of.
//
// The panel is a grid of tiles over a "bench" holding the selected recipe. Three ideas carry it at the
// 40-50 recipes the full game is heading for, where a list of cards would be several screens long:
//
//   * FAMILIES. Kinds of one thing fold into one tile (every wall, both daggers, the metal bars), and
//     the kind is picked on the bench. A recipe's family comes from content (presentation.crafting).
//   * READY. A shelf over the groups holding everything that can be made right now -- the question a
//     player opens this screen with. It replaced the "Can make now" filter: the goals stay on screen.
//   * A FIXED ORDER inside each group. A grid is learned by position, so a tile never moves because the
//     pack changed. What changed is shown on the tile (lit, a count), not by where it sits.
//
// Pure of pixi on purpose, like crafting_grid.ts beside it -- see __tests__/crafting_catalog.test.ts.

/**
 * One ingredient of a recipe: what it costs, how much of it the player holds, and -- for one they are
 * short of -- what can be done about it. Mirrors game-common's IngredientCost; restated rather than
 * imported so this module stays free of the item/mechanics import cycle.
 */
export interface IngredientCost {
    itemType: string
    required: number
    held: number
    // Does this material have a recipe of its own? Then a shortfall is a tile to go to, not a hunt.
    craftable?: boolean
    // Where the stuff comes from, for a material nothing makes ("Chop trees").
    source?: string
    // Its art, for the ingredient's tile on the bench.
    spriteId?: string
}

export const isShort = (cost: IngredientCost): boolean => cost.held < cost.required

/** A recipe as the bench needs it: the server's row, plus the words content gives it. */
export interface BenchRecipe {
    // The server's name for the row, unique per recipe ("dagger (metal)"). The selection is kept by it.
    key: string
    itemType: string
    // What a player calls it ("Iron Dagger"). Falls back to the item type, prettified.
    title: string
    group: string
    family?: string
    // The shared tile's name ("Dagger"), when the family has more than one member on the list.
    familyTitle?: string
    // This member's name on the bench's kind picker ("Iron").
    variant?: string
    description?: string
    // The Craft button is live: the price is covered and the pack has room.
    enabled: boolean
    // Covered, but nowhere to put what it makes.
    full?: boolean
    cost: IngredientCost[]
    // How many the player carries already.
    owned: number
    // How many one craft makes.
    yield: number
    // How many crafts the pack covers.
    maxCrafts: number
    spriteId?: string
}

/** A tile: one family (or one lone recipe), showing one of its members. */
export interface CatalogTile {
    // The tile's identity across rebuilds and between the Ready shelf and its home group.
    family: string
    title: string
    shown: BenchRecipe
    members: BenchRecipe[]
    // Something on this tile can be made now.
    lit: boolean
    // Carried across every member.
    owned: number
    // 0..1 toward the shown member's price: how close an unlit tile is. 1 when lit.
    progress: number
}

export interface CatalogGroup {
    key: string
    label: string
    tiles: CatalogTile[]
}

export const READY_GROUP = "ready"
export const READY_LABEL = "Ready"

export const familyOf = (recipe: BenchRecipe): string => recipe.family ?? `recipe:${recipe.key}`

const normalise = (value?: string): string => (value || "").toLowerCase().replace(/[-_]/g, " ").trim()

/**
 * Does this recipe match what was typed? Its name, its item type, and its INGREDIENTS -- "rope" finds
 * the rope and also the snare that needs one, which is the half of a search that earns the box.
 */
export const matchesQuery = (recipe: BenchRecipe, query: string): boolean => {
    const needle = normalise(query)
    if (!needle) {
        return true
    }
    if ([recipe.title, recipe.key, recipe.itemType, recipe.familyTitle].some(v => normalise(v).includes(needle))) {
        return true
    }
    return recipe.cost.some(ingredient => normalise(ingredient.itemType).includes(needle))
}

/** How close the player is to affording it: the mean of each ingredient's covered share. */
export const progressToward = (recipe: BenchRecipe): number => {
    if (recipe.enabled) {
        return 1
    }
    if (recipe.cost.length < 1) {
        return 0
    }
    const covered = recipe.cost.reduce((sum, c) => sum + Math.min(1, c.held / Math.max(1, c.required)), 0)
    return covered / recipe.cost.length
}

const tileFor = (members: BenchRecipe[], shown: BenchRecipe): CatalogTile => ({
    family: familyOf(shown),
    title: members.length > 1 ? shown.familyTitle ?? shown.title : shown.title,
    shown,
    members,
    lit: members.some(m => m.enabled),
    owned: members.reduce((sum, m) => sum + (m.owned || 0), 0),
    progress: progressToward(shown),
})

/**
 * Every tile, grouped: the Ready shelf first (when anything is ready), then the groups in the order
 * given, recipes inside each in the order the server sent them.
 *
 * Which member a tile SHOWS: the selected recipe if it is one of them, else the kind the player last
 * picked for that family, else the first. On the Ready shelf only members that can be made count.
 */
export const buildCatalog = ({
    recipes,
    groups,
    selectedKey,
    picks = {},
    query = "",
}: {
    recipes: BenchRecipe[]
    groups: { key: string; label: string }[]
    selectedKey?: string
    picks?: Record<string, string>
    query?: string
}): CatalogGroup[] => {
    // Members in list order, so a family's first member is its first recipe.
    const families = new Map<string, BenchRecipe[]>()
    recipes.forEach(recipe => {
        const family = familyOf(recipe)
        families.set(family, [...(families.get(family) || []), recipe])
    })
    const choose = (members: BenchRecipe[]): BenchRecipe =>
        members.find(m => m.key === selectedKey) ?? members.find(m => m.key === picks[familyOf(m)]) ?? members[0]

    // A family is shown if any member matches the search; it is listed under its first member's group.
    const shown = [...families.values()].filter(members => members.some(m => matchesQuery(m, query)))

    const out: CatalogGroup[] = []
    const ready = shown
        .filter(members => members.some(m => m.enabled))
        .map(members => tileFor(members, choose(members.filter(m => m.enabled))))
    if (ready.length) {
        out.push({ key: READY_GROUP, label: READY_LABEL, tiles: ready })
    }

    const order = [...groups]
    shown.forEach(members => {
        if (!order.some(g => g.key === members[0].group)) {
            order.push({ key: members[0].group, label: members[0].group })
        }
    })
    order.forEach(group => {
        const tiles = shown.filter(members => members[0].group === group.key).map(members => tileFor(members, choose(members)))
        if (tiles.length) {
            out.push({ key: group.key, label: group.label, tiles })
        }
    })
    return out
}

/**
 * What has become makeable since the last list: each recipe enabled now that was not before. Empty on
 * the first list a panel sees -- arriving at the bench is not an unlock.
 */
export const newlyEnabled = (before: Set<string> | undefined, recipes: BenchRecipe[]): BenchRecipe[] =>
    before ? recipes.filter(r => r.enabled && !before.has(r.key)) : []

export const enabledKeys = (recipes: BenchRecipe[]): Set<string> => new Set(recipes.filter(r => r.enabled).map(r => r.key))

/** The recipe that makes this item, preferring one that can be made now. */
export const recipeForItem = (recipes: BenchRecipe[], itemType: string): BenchRecipe | undefined =>
    recipes.find(r => r.itemType === itemType && r.enabled) ?? recipes.find(r => r.itemType === itemType)

/**
 * "Make Planks first": the first thing this recipe is short of that the player can make right now.
 * It turns a dead Craft button into the next step of the plan, without leaving the bench.
 */
export const firstStep = (recipe: BenchRecipe, recipes: BenchRecipe[]): BenchRecipe | undefined => {
    for (const cost of recipe.cost) {
        if (!isShort(cost)) {
            continue
        }
        const maker = recipes.find(r => r.itemType === cost.itemType && r.enabled)
        if (maker) {
            return maker
        }
    }
    return undefined
}

/**
 * Why the Craft button is dead, in one line: the pack, or the first two things it is short of
 * ("Need 3 more planks"). `nameOf` turns an item type into words.
 */
export const needLine = (recipe: BenchRecipe, nameOf: (itemType: string) => string): string => {
    if (recipe.enabled) {
        return ""
    }
    const shorts = recipe.cost.filter(isShort)
    if (shorts.length < 1) {
        return recipe.full ? "Backpack full" : ""
    }
    return `Need ${shorts.slice(0, 2).map(c => `${c.required - c.held} more ${nameOf(c.itemType).toLowerCase()}`).join(", ")}`
}

/** Where to find what nothing makes: "Wood: chop trees". Only for materials the player is short of. */
export const sourceLine = (recipe: BenchRecipe, nameOf: (itemType: string) => string): string =>
    recipe.cost
        .filter(c => isShort(c) && !c.craftable && c.source)
        .map(c => `${nameOf(c.itemType)}: ${c.source!.charAt(0).toLowerCase()}${c.source!.slice(1)}`)
        .join(" · ")
